package com.iregained.site.components.sections.corporate.sections

import androidx.compose.runtime.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Block Reveal
@Composable
private fun Modifier.staggeredBlockReveal(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 20.px).transition(
        Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun TermsOfServiceSection(data: TermsOfServiceSectionData = defaultTermsOfServiceData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "terms-of-service-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.blocks.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1000.px), horizontalAlignment = Alignment.Start
                ) {
                    if (data.pageTitle.isNotBlank()) {
                        SectionHeader(
                            text = data.pageTitle,
                            fontSize = 32.px,
                            lineWidthPercent = 15.percent,
                            modifier = Modifier.margin(bottom = 8.px).staggeredBlockReveal(isVisible, 0),
                            alignment = Alignment.Start
                        )
                    }

                    if (data.lastUpdatedText.isNotBlank()) {
                        Div(
                            attrs = Modifier.fontSize(14.px).color(palette.brand.primary).fontWeight(FontWeight.Bold)
                                .margin(bottom = 32.px).staggeredBlockReveal(isVisible, 100).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.lastUpdatedText
                                        onDispose {}
                                    }
                                })
                    }

                    data.blocks.forEachIndexed { index, block ->
                        key("tos_block_$index") {
                            Column(
                                modifier = Modifier.fillMaxWidth().margin(bottom = 28.px)
                                    .staggeredBlockReveal(isVisible, 200 + (index * 100))
                            ) {
                                if (block.heading.isNotBlank()) {
                                    Div(
                                        attrs = Modifier.fontSize(20.px).color(palette.brand.primary)
                                            .fontWeight(FontWeight.Bold).margin(bottom = 12.px).toAttrs {
                                                ref { htmlElement ->
                                                    htmlElement.innerHTML = block.heading
                                                    onDispose {}
                                                }
                                            })
                                }

                                if (block.contentHtml.isNotBlank()) {
                                    Div(
                                        attrs = Modifier.color(palette.custom.darkText).fontSize(16.px).lineHeight(1.6)
                                            .toAttrs { ref { el -> el.innerHTML = block.contentHtml; onDispose {} } })
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// DATA MODELS & DEFAULT PAYLOADS
// ==========================================

data class TosBlockData(val heading: String, val contentHtml: String)

data class TermsOfServiceSectionData(
    val sectionId: String? = null,
    val pageTitle: String = "TERMS OF SERVICE",
    val lastUpdatedText: String = "Effective Date: May, 2026 | Version: 1.0 (Resource Portal Focus)",
    val blocks: List<TosBlockData>
)

val defaultTermsOfServiceData = TermsOfServiceSectionData(
    blocks = listOf(
        TosBlockData(
            heading = "",
            contentHtml = "<p>Welcome to the IRegained Inc. (\"IRegained\", \"we\", \"our\", \"us\") digital resource portal. By accessing or using our website (www.iregained.rehab) and associated informational domains, you agree to comply with and be bound by these Terms of Service.</p><p>If you do not agree to these terms, please do not use our website.</p>"
        ), TosBlockData(
            heading = "1. Informational Scope & No Medical Advice",
            contentHtml = "<p><b>Resource Only:</b> The content provided on this website—including mechatronic specifications, clinical summaries, recovery stories, and whitepapers—is for informational and educational purposes only.</p><p><b>No Clinical Interface:</b> This website does not provide direct medical advice, diagnosis, or digital neurotherapeutics (DTx) therapy. The physical deployment of the MyHand<sup>®</sup> System is strictly governed by separate Master Services Agreements, End-User License Agreements (EULAs), and clinical prescriptions overseen by certified healthcare professionals.</p>"
        ), TosBlockData(
            heading = "2. Intellectual Property Rights",
            contentHtml = "<p>All content, digital assets, visual designs, software workflows, and mechatronic documentation displayed on this website are the exclusive property of IRegained Inc. or its licensors, protected by Canadian and international copyright, trademark, and patent laws.</p><ul><li><b>Trademarks:</b> \"IRegained\", \"MyHand<sup>®</sup>\", and associated logos are registered trademarks of IRegained Inc. Unauthorized reproduction or commercial presentation is strictly prohibited.</li><li><b>Limited License:</b> You are granted a limited, non-exclusive, non-transferable license to view, download, and print published resources strictly for personal educational evaluation or internal clinical procurement review.</li></ul>"
        ), TosBlockData(
            heading = "3. Acceptable Website Use",
            contentHtml = "<p>When interacting with our portal, booking workflows, or pre-approval applications, you agree not to:</p><ul><li>Use automated scripts, scrapers, or crawlers to harvest proprietary clinical data, partner configurations, or code architectures.</li><li>Attempt to gain unauthorized access to secure member zones, distributor portals, or underlying web server environments.</li><li>Submit false, misleading, or fraudulent facility details through our procurement routing forms.</li></ul>"
        ), TosBlockData(
            heading = "4. Third-Party Integrations & Procurement Portals",
            contentHtml = "<p>Our website provides direct operational routing to external leasing platforms (such as VendorLender / Econolease). Your interaction with these third-party financial applications is governed entirely by their respective terms of use and data collection mandates. IRegained Inc. assumes no liability for external credit evaluations, approval decisions, or third-party platform uptime.</p>"
        ), TosBlockData(
            heading = "5. Disclaimer of Warranties & Limitation of Liability",
            contentHtml = "<p><b>\"As Is\" Presentation:</b> While we strive to maintain accurate, up-to-date scientific and product information, this website is provided on an \"as is\" and \"as available\" basis without warranties of any kind, whether express or implied.</p><p><b>Limitation of Liability:</b> To the fullest extent permissible by applicable law, IRegained Inc., its leadership, and its affiliates shall not be held liable for any indirect, incidental, consequential, or punitive damages arising out of your access to, use of, or inability to use this digital resource portal.</p>"
        ), TosBlockData(
            heading = "6. Governing Law & Jurisdiction",
            contentHtml = "<p>These Terms of Service are governed by and construed in accordance with the laws of the <b>Province of Ontario</b> and the federal laws of Canada applicable therein, without regard to conflict of law principles. Any legal proceedings arising directly from website utilization shall be resolved exclusively in the appropriate courts located in <b>Sudbury, Ontario, Canada</b>.</p>"
        ), TosBlockData(
            heading = "7. Contact Legal Administration",
            contentHtml = "<p>If you have questions regarding these Terms of Service or require formal corporate usage permissions, please contact our administrative offices:</p><p>IRegained Inc. Legal Department<br>1545 Maley Drive, Sudbury, ON, P3A 4R7<br>Email: info@iregained.rehab</p><p><i>© 2026 IRegained Inc. | All Rights Reserved</i></p>"
        )
    )
)